/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { AuthenticatedRequestDto } from '../models/authenticated-request-dto';
import { BasketDtoGenericResponse } from '../models/basket-dto-generic-response';
import { BooleanGenericResponse } from '../models/boolean-generic-response';
import { CheckCouponRequest } from '../models/check-coupon-request';
import { GuidGenericResponse } from '../models/guid-generic-response';
import { ProcessBasketRequestDto } from '../models/process-basket-request-dto';
import { ProcessBasketResponseGenericResponse } from '../models/process-basket-response-generic-response';

@Injectable({ providedIn: 'root' })
export class BasketService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `apiV1BasketProcessPost()` */
  static readonly ApiV1BasketProcessPostPath = '/api/v1/basket/process';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `apiV1BasketProcessPost$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  apiV1BasketProcessPost$Plain$Response(
    params?: {
      body?: ProcessBasketRequestDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ProcessBasketResponseGenericResponse>> {
    const rb = new RequestBuilder(this.rootUrl, BasketService.ApiV1BasketProcessPostPath, 'post');
    if (params) {
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(
      rb.build({ responseType: 'text', accept: 'text/plain', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ProcessBasketResponseGenericResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `apiV1BasketProcessPost$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  apiV1BasketProcessPost$Plain(
    params?: {
      body?: ProcessBasketRequestDto
    },
    context?: HttpContext
  ): Observable<ProcessBasketResponseGenericResponse> {
    return this.apiV1BasketProcessPost$Plain$Response(params, context).pipe(
      map((r: StrictHttpResponse<ProcessBasketResponseGenericResponse>): ProcessBasketResponseGenericResponse => r.body)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `apiV1BasketProcessPost$Json()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  apiV1BasketProcessPost$Json$Response(
    params?: {
      body?: ProcessBasketRequestDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ProcessBasketResponseGenericResponse>> {
    const rb = new RequestBuilder(this.rootUrl, BasketService.ApiV1BasketProcessPostPath, 'post');
    if (params) {
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'text/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ProcessBasketResponseGenericResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `apiV1BasketProcessPost$Json$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  apiV1BasketProcessPost$Json(
    params?: {
      body?: ProcessBasketRequestDto
    },
    context?: HttpContext
  ): Observable<ProcessBasketResponseGenericResponse> {
    return this.apiV1BasketProcessPost$Json$Response(params, context).pipe(
      map((r: StrictHttpResponse<ProcessBasketResponseGenericResponse>): ProcessBasketResponseGenericResponse => r.body)
    );
  }

  /** Path part for operation `apiV1BasketClearBasketIdPost()` */
  static readonly ApiV1BasketClearBasketIdPostPath = '/api/v1/basket/clear/{basketId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `apiV1BasketClearBasketIdPost$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  apiV1BasketClearBasketIdPost$Plain$Response(
    params: {
      basketId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GuidGenericResponse>> {
    const rb = new RequestBuilder(this.rootUrl, BasketService.ApiV1BasketClearBasketIdPostPath, 'post');
    if (params) {
      rb.path('basketId', params.basketId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'text', accept: 'text/plain', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<GuidGenericResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `apiV1BasketClearBasketIdPost$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  apiV1BasketClearBasketIdPost$Plain(
    params: {
      basketId: string;
    },
    context?: HttpContext
  ): Observable<GuidGenericResponse> {
    return this.apiV1BasketClearBasketIdPost$Plain$Response(params, context).pipe(
      map((r: StrictHttpResponse<GuidGenericResponse>): GuidGenericResponse => r.body)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `apiV1BasketClearBasketIdPost$Json()` instead.
   *
   * This method doesn't expect any request body.
   */
  apiV1BasketClearBasketIdPost$Json$Response(
    params: {
      basketId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GuidGenericResponse>> {
    const rb = new RequestBuilder(this.rootUrl, BasketService.ApiV1BasketClearBasketIdPostPath, 'post');
    if (params) {
      rb.path('basketId', params.basketId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'text/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<GuidGenericResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `apiV1BasketClearBasketIdPost$Json$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  apiV1BasketClearBasketIdPost$Json(
    params: {
      basketId: string;
    },
    context?: HttpContext
  ): Observable<GuidGenericResponse> {
    return this.apiV1BasketClearBasketIdPost$Json$Response(params, context).pipe(
      map((r: StrictHttpResponse<GuidGenericResponse>): GuidGenericResponse => r.body)
    );
  }

  /** Path part for operation `apiV1BasketBasketIdPost()` */
  static readonly ApiV1BasketBasketIdPostPath = '/api/v1/basket/{basketId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `apiV1BasketBasketIdPost$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  apiV1BasketBasketIdPost$Plain$Response(
    params: {
      basketId: string;
      body?: AuthenticatedRequestDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<BasketDtoGenericResponse>> {
    const rb = new RequestBuilder(this.rootUrl, BasketService.ApiV1BasketBasketIdPostPath, 'post');
    if (params) {
      rb.path('basketId', params.basketId, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(
      rb.build({ responseType: 'text', accept: 'text/plain', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BasketDtoGenericResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `apiV1BasketBasketIdPost$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  apiV1BasketBasketIdPost$Plain(
    params: {
      basketId: string;
      body?: AuthenticatedRequestDto
    },
    context?: HttpContext
  ): Observable<BasketDtoGenericResponse> {
    return this.apiV1BasketBasketIdPost$Plain$Response(params, context).pipe(
      map((r: StrictHttpResponse<BasketDtoGenericResponse>): BasketDtoGenericResponse => r.body)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `apiV1BasketBasketIdPost$Json()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  apiV1BasketBasketIdPost$Json$Response(
    params: {
      basketId: string;
      body?: AuthenticatedRequestDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<BasketDtoGenericResponse>> {
    const rb = new RequestBuilder(this.rootUrl, BasketService.ApiV1BasketBasketIdPostPath, 'post');
    if (params) {
      rb.path('basketId', params.basketId, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'text/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BasketDtoGenericResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `apiV1BasketBasketIdPost$Json$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  apiV1BasketBasketIdPost$Json(
    params: {
      basketId: string;
      body?: AuthenticatedRequestDto
    },
    context?: HttpContext
  ): Observable<BasketDtoGenericResponse> {
    return this.apiV1BasketBasketIdPost$Json$Response(params, context).pipe(
      map((r: StrictHttpResponse<BasketDtoGenericResponse>): BasketDtoGenericResponse => r.body)
    );
  }

  /** Path part for operation `apiV1BasketCouponCheckPost()` */
  static readonly ApiV1BasketCouponCheckPostPath = '/api/v1/basket/coupon/check';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `apiV1BasketCouponCheckPost$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  apiV1BasketCouponCheckPost$Plain$Response(
    params?: {
      body?: CheckCouponRequest
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<BooleanGenericResponse>> {
    const rb = new RequestBuilder(this.rootUrl, BasketService.ApiV1BasketCouponCheckPostPath, 'post');
    if (params) {
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(
      rb.build({ responseType: 'text', accept: 'text/plain', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BooleanGenericResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `apiV1BasketCouponCheckPost$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  apiV1BasketCouponCheckPost$Plain(
    params?: {
      body?: CheckCouponRequest
    },
    context?: HttpContext
  ): Observable<BooleanGenericResponse> {
    return this.apiV1BasketCouponCheckPost$Plain$Response(params, context).pipe(
      map((r: StrictHttpResponse<BooleanGenericResponse>): BooleanGenericResponse => r.body)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `apiV1BasketCouponCheckPost$Json()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  apiV1BasketCouponCheckPost$Json$Response(
    params?: {
      body?: CheckCouponRequest
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<BooleanGenericResponse>> {
    const rb = new RequestBuilder(this.rootUrl, BasketService.ApiV1BasketCouponCheckPostPath, 'post');
    if (params) {
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'text/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BooleanGenericResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `apiV1BasketCouponCheckPost$Json$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  apiV1BasketCouponCheckPost$Json(
    params?: {
      body?: CheckCouponRequest
    },
    context?: HttpContext
  ): Observable<BooleanGenericResponse> {
    return this.apiV1BasketCouponCheckPost$Json$Response(params, context).pipe(
      map((r: StrictHttpResponse<BooleanGenericResponse>): BooleanGenericResponse => r.body)
    );
  }

}
